import { cn } from '@dialogueconsulting/sked-ui/lib/utils'
import { format } from 'date-fns'
import { useState } from 'react'
import { useCollapse } from 'react-collapsed'

import type { ConversationProps, PostActionProps } from 'Components'
import { Conversation, PostAction } from 'Components'
import type { PlatformIconProps } from 'Components/Common/PlatformIcon'
import { datePlaceholderForDraftPost } from 'normalizer'
import type { PreviewVariant } from 'utils/postPreviewVariant'

import SkedPreviewFrame from '../SkedPreviewFrame'
import { StatusLabel } from '../post'

export interface MobileUpcomingPostCardProps {
  id: string
  status: string
  statusLabel?: string
  time: string
  when?: string
  platform?: PlatformIconProps['platform']
  skedPreviewPost: any
  conversation: ConversationProps
  postAction: PostActionProps
  isReadOnly: boolean
  previewVariant?: PreviewVariant
  updatePostState: (
    reviewKey: string,
    postId: string,
    finalState: string
  ) => void
}

const MobileUpcomingPostCard = (props: MobileUpcomingPostCardProps) => {
  const {
    id,
    status,
    statusLabel,
    time,
    when = '',
    platform,
    skedPreviewPost,
    conversation,
    postAction,
    isReadOnly,
    previewVariant = 'legacy',
    updatePostState,
  } = props
  const { getCollapseProps, getToggleProps, isExpanded } = useCollapse({
    defaultExpanded: true,
  })

  const isDisplayTime =
    status !== 'requestChange' &&
    time !== datePlaceholderForDraftPost &&
    !isReadOnly

  let formattedDate = ''
  if (when) {
    formattedDate = format(new Date(when), 'EEEE, do LLLL HH:mm aa ')
  }

  // local status that don't persist after user refresh the page
  const [localPostStatus, setLocalPostStatus] = useState<string>('pending')

  const handleUpdatePostState = (key: any, postId: any, postStatus: any) => {
    setLocalPostStatus?.(postStatus)
    updatePostState(key, postId, postStatus)
  }

  return (
    <div
      className={cn(
        'relative mb-4 min-h-[38px] rounded-[5px] border',
        isExpanded
          ? 'border-[#dedafe] bg-[#fbfbff]'
          : 'border-[#eeeeee] bg-white'
      )}
    >
      <div>
        <div {...getToggleProps()}>
          <div className="flex h-9 items-center pr-5 [&>div:first-child]:-left-px [&>div:first-child]:-top-px">
            <StatusLabel
              className="w-full"
              postId={id}
              status={status}
              statusLabel={statusLabel}
              platform={platform}
              time={time}
              isMobile={true}
              isReadOnly={isReadOnly}
              isDisplayTime={isDisplayTime}
              isExpanded={isExpanded}
              updatePostState={handleUpdatePostState}
            />
          </div>
        </div>

        <div {...getCollapseProps({ className: 'p-2' })}>
          {status !== 'pending' && (
            <div className="absolute left-0 z-[1] w-full rounded-[5px] bg-white opacity-40" />
          )}
          {skedPreviewPost && (
            <>
              <div className="flex h-9 w-full items-center justify-center pb-1">
                {formattedDate}
              </div>
              <SkedPreviewFrame
                post={skedPreviewPost}
                previewVariant={previewVariant}
                legacyClassName="mx-auto mb-[18px] rounded-[10px] bg-white p-[3px] shadow-[3px_0_10px_rgba(0,0,0,0.15)]"
                upliftWrapperClassName="mb-[18px] overflow-x-auto"
              />
            </>
          )}

          {!isReadOnly && (
            <PostAction
              {...postAction}
              className="my-[10px]"
              postId={id}
              isMobile
              localPostStatus={localPostStatus}
            />
          )}

          {!isReadOnly && (
            <>
              <Conversation {...conversation} isMobile status={status} />

              {!postAction.isLoading && (
                <hr
                  className={cn(
                    // border-0 (not border-none): resets the <hr> preflight
                    // border WIDTH to 0 while keeping border-style: solid, so
                    // the conditional border-b below actually renders.
                    'border-0',
                    status === 'pending' &&
                      'mx-auto mb-4 mt-3 border-b border-[#dedafe]'
                  )}
                />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default MobileUpcomingPostCard
